import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, Eye, TrendingUp, Heart } from "lucide-react";
import { highlightRepeatedSequencesSafe, createSafeHtmlProps } from "@/utils/sanitize";
import { useUserNumerologyNumbers } from "@/hooks/useUserNumerologyNumbers";

interface ResultSectionProps {
  resultado: {
    triangulo: string;
    numeroFinal: number;
    explicacao: string;
  };
  onViewComplete: () => void;
}

const ResultSection = ({ resultado, onViewComplete }: ResultSectionProps) => {
  const { destino, expressao, nomeCompleto, hasData } = useUserNumerologyNumbers();

  // Detectar sequências na pirâmide
  const detectSequences = (triangulo: string): number[] => {
    if (!triangulo) return [];
    
    const sequences: number[] = [];
    // Regex para detectar 3+ dígitos iguais separados por NBSP ou espaço
    const NBSP = '(?:&nbsp;|\\u00A0|\\s)';
    
    for (let digit = 1; digit <= 9; digit++) {
      const pattern = new RegExp(`${digit}(${NBSP}${digit}){2,}`, 'g');
      if (pattern.test(triangulo)) {
        sequences.push(digit);
      }
    }
    
    return sequences;
  };

  // Teasers específicos por sequência encontrada
  const getSequenceTeasers = (triangulo: string, numeroFinal: number) => {
    const sequences = detectSequences(triangulo);
    
    const sequenceTeasers: Record<number, { emoji: string; title: string; prosperidade: string; relacionamentos: string; saude: string; cta: string }> = {
      1: {
        emoji: "⚡",
        title: "Pressão e Solidão",
        prosperidade: "excesso de cobrança te faz perder oportunidades porque está sempre exausta.",
        relacionamentos: "sensação de estar sozinha até acompanhada; dificuldade de confiar no outro.",
        saude: "ansiedade, estresse e insônia.",
        cta: "No Mapa Completo, você aprende a transformar essa pressão em liderança saudável e prosperidade real."
      },
      2: {
        emoji: "🤐",
        title: "Relações Desiguais",
        prosperidade: "trava decisões financeiras porque precisa da validação dos outros.",
        relacionamentos: "relações de dependência, submissão e desvalorização.",
        saude: "baixa autoestima gera crises de ansiedade e até doenças emocionais.",
        cta: "No Mapa Completo, você descobre como recuperar confiança e equilíbrio em todas as áreas."
      },
      3: {
        emoji: "🔇",
        title: "Voz Bloqueada",
        prosperidade: "boas ideias morrem por medo de crítica, gerando frustração e perdas.",
        relacionamentos: "dificuldade de se impor e ser ouvido destrói a intimidade.",
        saude: "sufoco interno se reflete em ansiedade e sintomas respiratórios.",
        cta: "No Mapa Completo, você aprende a usar sua voz para destravar dinheiro, amor e bem-estar."
      },
      4: {
        emoji: "🌀",
        title: "Vida Caótica",
        prosperidade: "falta de disciplina gera dívidas, projetos inacabados e instabilidade.",
        relacionamentos: "ausência de rotina saudável causa conflitos e afastamentos.",
        saude: "desordem aumenta o estresse, afetando ossos, articulações e imunidade.",
        cta: "No Mapa Completo, você recebe ferramentas para criar ordem e colher prosperidade."
      },
      5: {
        emoji: "⛓️",
        title: "Presa no Mesmo Lugar",
        prosperidade: "medo da mudança mantém você em empregos ou negócios sem futuro.",
        relacionamentos: "repete vínculos tóxicos por medo do novo.",
        saude: "estagnação gera desânimo, sedentarismo e bloqueios energéticos.",
        cta: "No Mapa Completo, você descobre como se libertar para prosperar e viver relações saudáveis."
      },
      6: {
        emoji: "💔",
        title: "Amor que Sufoca",
        prosperidade: "excesso de preocupação com os outros te faz negligenciar sua vida financeira.",
        relacionamentos: "entrega sem limites leva ao desgaste e à sensação de ser usada.",
        saude: "esgotamento emocional → problemas hormonais, renais e reprodutivos.",
        cta: "No Mapa Completo, você aprende a se priorizar sem culpa, gerando equilíbrio e abundância."
      },
      7: {
        emoji: "🌑",
        title: "Vazio Interior",
        prosperidade: "sem clareza interna, toma decisões erradas e perde chances financeiras.",
        relacionamentos: "isolamento e superficialidade impedem conexões verdadeiras.",
        saude: "vazio existencial gera ansiedade, problemas de pele e nervos.",
        cta: "No Mapa Completo, você descobre como se reconectar e prosperar com sentido."
      },
      8: {
        emoji: "💸",
        title: "Dinheiro Escorrendo",
        prosperidade: "luta constante sem acúmulo, sensação de escassez eterna.",
        relacionamentos: "conflitos de poder, submissão ou autoritarismo.",
        saude: "pressão alta, problemas circulatórios e estresse crônico.",
        cta: "No Mapa Completo, você aprende a ativar sua prosperidade e assumir controle da vida."
      },
      9: {
        emoji: "🔗",
        title: "Ciclos que se Repetem",
        prosperidade: "dívidas voltam, negócios não se consolidam, estagnação financeira.",
        relacionamentos: "reviver ex, repetir erros amorosos, dificuldade em desapegar.",
        saude: "peso do passado gera fadiga crônica, problemas nos pés e sistema linfático.",
        cta: "No Mapa Completo, você aprende a encerrar ciclos e abrir espaço para prosperidade, saúde e amor verdadeiro."
      }
    };

    // Se encontrou sequências, retorna todos os teasers específicos
    if (sequences.length > 0) {
      return sequences.map(seq => sequenceTeasers[seq]).filter(Boolean);
    }

    // Análise de alinhamento energético para nomes sem sequências negativas
    const getAlignmentAnalysis = () => {
      if (!hasData || !destino || !expressao) {
        return [
          {
            emoji: "⚖️",
            title: "Alinhamento Energético",
            prosperidade: "mesmo sem bloqueios, o alinhamento entre seu nome e destino pode potencializar sua prosperidade",
            relacionamentos: "harmonia entre expressão e propósito gera relacionamentos mais autênticos",
            saude: "energia alinhada promove bem-estar e clareza mental",
            cta: "No Mapa Completo, você descobre como otimizar seu alinhamento energético para máximos resultados."
          }
        ];
      }

      const destinoNum = destino;
      const expressaoNum = expressao;
      const numeroFinalPiramide = numeroFinal;

      // Detectar tipos de desalinhamento
      const diferenca = Math.abs(destinoNum - expressaoNum);
      const isConflittoEspiritualMaterial = 
        (destinoNum === 7 && expressaoNum === 8) || 
        (destinoNum === 8 && expressaoNum === 7) ||
        (destinoNum === 9 && expressaoNum === 1);

      const isDesconexaoInterna = diferenca >= 4;
      const isDesequilibrioSutil = diferenca >= 2 && diferenca < 4;

      if (isConflittoEspiritualMaterial) {
        return [
          {
            emoji: "⚖️",
            title: "Conflito Espiritual vs Material",
            prosperidade: "busca estabilidade espiritual mas sente cobrança constante para alcançar resultados materiais",
            relacionamentos: "conflito entre valores pessoais profundos e expectativas sociais gera tensão",
            saude: "estresse interno por viver dividida entre dois mundos causa ansiedade e fadiga",
            cta: "No Mapa Completo, você aprende como harmonizar sua energia espiritual com prosperidade material."
          },
          {
            emoji: "🔄",
            title: "Energia Dispersa",
            prosperidade: "tem oportunidades mas não se sente plenamente realizada financeiramente",
            relacionamentos: "sensação de que algo 'não encaixa' nas relações pessoais e profissionais",
            saude: "inquietação interna e busca constante por algo que falta",
            cta: "Descubra como alinhar sua expressão com seu verdadeiro destino para maior realização."
          }
        ];
      }

      if (isDesconexaoInterna) {
        return [
          {
            emoji: "🔄",
            title: "Desconexão Interna",
            prosperidade: "tem potencial e oportunidades mas não consegue se sentir totalmente realizada",
            relacionamentos: "dificuldade de encontrar pessoas que realmente te entendem em profundidade",
            saude: "sensação constante de que algo importante está faltando na vida",
            cta: "No Mapa Completo, você aprende a alinhar sua energia para maior propósito e satisfação."
          },
          {
            emoji: "⚡",
            title: "Potencial Fragmentado",
            prosperidade: "energia dispersa dificulta a concretização de projetos e objetivos financeiros",
            relacionamentos: "apresenta faces diferentes dependendo do ambiente, causando superficialidade",
            saude: "instabilidade emocional e dificuldade de manter consistência",
            cta: "Descubra como integrar todas as suas facetas para maximizar seu potencial."
          }
        ];
      }

      if (isDesequilibrioSutil) {
        return [
          {
            emoji: "🎯",
            title: "Desequilíbrio Sutil",
            prosperidade: "pequenos desajustes energéticos limitam seu potencial máximo de prosperidade",
            relacionamentos: "relacionamentos bons mas que poderiam ser ainda mais profundos e realizadores",
            saude: "bem-estar geral mas com momentos de desânimo ou falta de direção",
            cta: "No Mapa Completo, você aprende os ajustes precisos para alcançar seu máximo potencial."
          }
        ];
      }

      // Caso especial: números próximos mas ainda precisam de alinhamento
      return [
        {
          emoji: "✨",
          title: "Refinamento Energético",
          prosperidade: "sua energia está bem alinhada, mas pequenos ajustes podem multiplicar seus resultados",
          relacionamentos: "conexões autênticas podem se tornar ainda mais profundas e transformadoras",
          saude: "equilíbrio mental e físico pode evoluir para um estado de excelência",
          cta: "No Mapa Completo, você descobre como refinar sua energia para viver em estado de fluxo."
        },
        {
          emoji: "🚀",
          title: "Potencial de Expansão",
          prosperidade: "mesmo sem bloqueios, há caminhos para acelerar sua prosperidade exponencialmente",
          relacionamentos: "oportunidade de se tornar uma pessoa ainda mais magnética e inspiradora",
          saude: "vitalidade pode ser otimizada para níveis superiores de energia e clareza",
          cta: "Aprenda as estratégias avançadas para elevar sua vida a um novo patamar."
        }
      ];
    };

    return getAlignmentAnalysis();
  };

  const sequences = detectSequences(resultado.triangulo);
  const teasers = getSequenceTeasers(resultado.triangulo, resultado.numeroFinal);

  return (
    <section className="py-16 sm:py-20 md:py-32 relative">
      <div className="absolute inset-0 bg-gradient-to-r from-dark-bg-primary to-dark-bg-secondary" />
      <div className="container mx-auto px-4 sm:px-6 md:px-8 relative max-w-4xl">
        <Card className="card-modern !bg-slate-800/50 backdrop-blur-sm border border-white/10">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl text-white mb-4">
              🎯 Sua Análise Inicial Está Pronta!
            </CardTitle>
            <p className="text-gray-300 text-lg">
              Número Final: <span className="text-yellow-400 text-3xl font-bold">{resultado.numeroFinal}</span>
            </p>
          </CardHeader>
          <CardContent className="space-y-8">
            {/* Visualização da Pirâmide */}
            <div className="bg-muted/30 rounded-lg p-3 sm:p-4">
              <h4 className="font-semibold text-foreground mb-2 text-sm sm:text-base">Estrutura da Pirâmide:</h4>
              <div className="w-full overflow-x-auto">
                <pre
                  className="triangulo-container mx-auto text-center text-xs sm:text-sm text-white font-mono leading-relaxed bg-muted/20 rounded p-4"
                  {...createSafeHtmlProps(highlightRepeatedSequencesSafe(resultado.triangulo))}
                />
              </div>
            </div>

            {/* Seção de Teasers ou Explicação Educativa */}
            {sequences.length === 0 ? (
              // Explicação educativa detalhada para nomes sem sequências negativas
              <div className="bg-gradient-to-r from-slate-800/50 to-slate-700/50 rounded-lg p-8 border border-white/10 space-y-6">
                <div className="space-y-4 text-left">
                  <h3 className="text-xl font-semibold bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
                    🧩 Nem todos os nomes carregam sequências negativas. Nesses casos, pode parecer que não há necessidade de ajustes — mas a verdade é que o <u>alinhamento entre expressão, número de destino e carreira</u> continua sendo essencial.
                  </h3>
                  
                  <div className="space-y-4">
                    <p className="text-white">
                      <strong>Por quê?</strong> 🤔 Porque mesmo sem bloqueios, o nome pode estar vibrando em uma energia diferente ou desalinhada em relação ao propósito de vida.
                    </p>
                    
                    <div className="bg-slate-700/50 rounded-lg p-4 border-l-4 border-yellow-400">
                      <p className="text-white mb-2">
                        <strong>👉 Exemplo prático:</strong>
                      </p>
                      <p className="text-gray-300">
                        Uma pessoa tem um destino 7 (aprendizado, espiritualidade, pesquisa), mas a expressão vibra em 8 (poder material, liderança).
                        Mesmo sem sequências negativas, esse contraste pode gerar conflito interno: a pessoa busca estabilidade espiritual, mas sente cobrança constante para alcançar resultados materiais.
                      </p>
                    </div>
                    
                    <p className="text-white">
                      🔎 Da mesma forma, alguém pode ter um nome limpo, mas que não fortalece a energia necessária para destravar sua carreira ou apoiar sua verdadeira missão.
                    </p>
                    
                    <div className="space-y-3">
                      <p className="text-white font-semibold">
                        ❓ O que acontece nesse caso?
                      </p>
                      <ul className="space-y-2 text-gray-300 ml-4">
                        <li>• A pessoa não enfrenta bloqueios fortes, mas sente uma desconexão interna.</li>
                        <li>• Pode ter oportunidades, mas não se sentir realizada.</li>
                        <li>• Sente que algo "não encaixa" na profissão ou nas relações pessoais. 😕</li>
                      </ul>
                    </div>
                    
                    <div className="space-y-3">
                      <p className="text-white font-semibold">
                        ✅ O benefício do alinhamento mesmo sem sequências negativas
                      </p>
                      <p className="text-gray-300 mb-2">Quando o nome é harmonizado:</p>
                      <ul className="space-y-2 text-gray-300 ml-4">
                        <li>• A expressão fortalece o destino em vez de competir com ele.</li>
                        <li>• A energia do nome apoia as escolhas de carreira e missão de vida.</li>
                        <li>• A pessoa sente mais clareza, propósito e fluidez para conquistar resultados. ✨</li>
                      </ul>
                    </div>
                    
                    <div className="bg-gradient-to-r from-yellow-400/10 to-orange-400/10 rounded-lg p-4 border border-white/10">
                      <p className="text-white font-semibold">
                        📌 Em resumo: não ter sequências negativas não significa estar em equilíbrio total.
                      </p>
                      <p className="text-white mt-2">
                        O nome precisa vibrar em harmonia com o destino, <u>ajudando a abrir caminhos para prosperidade, realização e crescimento</u>. 🌟
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // Grid de teasers para quem tem sequências
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {teasers.map((teaser, index) => (
                  <div key={index} className="bg-gradient-to-r from-slate-800/50 to-slate-700/50 rounded-lg p-6 border border-white/10">
                    <div className="flex items-start gap-3 mb-4">
                      <div className="text-2xl">
                        {teaser.emoji}
                      </div>
                      <div className="flex-1">
                        <h4 className="text-white font-bold text-lg">
                          {teaser.title}
                        </h4>
                      </div>
                    </div>
                    
                    <div className="space-y-3 mb-4">
                      <div className="flex items-start gap-2">
                        <span className="text-lg">💰</span>
                        <div>
                          <p className="text-yellow-400 font-medium text-sm">Prosperidade:</p>
                          <p className="text-gray-300 text-sm">{teaser.prosperidade}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-2">
                        <span className="text-lg">❤️</span>
                        <div>
                          <p className="text-pink-400 font-medium text-sm">Relacionamentos:</p>
                          <p className="text-gray-300 text-sm">{teaser.relacionamentos}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-2">
                        <span className="text-lg">🩺</span>
                        <div>
                          <p className="text-green-400 font-medium text-sm">Saúde:</p>
                          <p className="text-gray-300 text-sm">{teaser.saude}</p>
                        </div>
                      </div>
                    </div>
                    
                    <p className="text-yellow-400 text-sm font-medium">
                      👉 {teaser.cta}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* Call to Action */}
            <div className="text-center bg-gradient-to-r from-yellow-400/10 to-orange-400/10 rounded-xl p-8 border border-yellow-400/30">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Eye className="h-6 w-6 text-yellow-400" />
                <h3 className="text-2xl font-bold text-white">Quer entender como transformar isso?</h3>
              </div>
              <p className="text-gray-300 text-lg mb-6">
                {sequences.length === 0 && hasData ? (
                  <>
                    <span className="block mb-3 text-gray-200 font-medium">
                      Nem todos os nomes carregam sequências negativas. Nesses casos, pode parecer que não há necessidade de ajustes — mas a verdade é que o alinhamento entre expressão, número de destino e carreira continua sendo essencial.
                    </span>
                    <span className="text-yellow-400 font-semibold">Por quê?</span> Porque mesmo sem bloqueios, o nome pode estar vibrando em uma energia diferente ou desalinhada em relação ao propósito de vida.
                  </>
                ) : (
                  <>No <span className="text-yellow-400 font-semibold">Mapa Completo</span> você descobre os passos exatos para liberar todo seu potencial.</>
                )}
              </p>
              <Button
                onClick={onViewComplete}
                size="lg"
                className="bg-gradient-to-r from-yellow-400 to-orange-400 hover:from-yellow-500 hover:to-orange-500 text-black font-bold px-8 py-4 text-lg h-14"
              >
                Ver Mapa Completo
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default ResultSection;